import { useCallback, useState } from 'react'

import { IContact, useReassignFlags } from 'clickNgo/entities/contact'
import { FlagToReassign, RoleToReassign } from 'clickNgo/features/contact/contactReassignment'
import { reassignContactRoles } from 'clickNgo/features/contact/contactReassignment/lib'
import { NotificationController } from 'core-components/library/popup'
import Logger from 'core/utils/Logger'

import { useContactData } from '../../../contactDeactivation/contexts'
import { handleRelinkingRemovingRoles } from '../../lib/handleRelinkingRemovingRoles'
import { ContactRelinkingError } from '../configs'
import { useReassignment } from '../contexts/ReassignmentContext'

interface UseContactDeactivationParams {
  contact: IContact
  rolesToReassign: RoleToReassign[]
  flagsToReassign: FlagToReassign[]
  onRelink: (newHpRoles?: string[]) => void
}

export const useContactRelink = ({
  rolesToReassign,
  flagsToReassign,
  onRelink,
}: UseContactDeactivationParams) => {
  const [loaderState, setLoaderState] = useState({ isLoading: false, message: '' })
  const { reassignFlags } = useReassignFlags()

  const { contact: relinkingContact } = useContactData()
  const {
    uniqueRolesToReassign,
    rolesForRemovingFromRelinkingContacts,
    targetsContactsWithRoleForRemoving,
  } = useReassignment()

  const relinkContact = useCallback(async () => {
    try {
      const rolesForSelectedContactForReassignment = [
        ...rolesToReassign,
        ...uniqueRolesToReassign,
      ].filter((el) => el.contact)
      if (rolesForSelectedContactForReassignment.length > 0) {
        setLoaderState({ isLoading: true, message: 'Reassigning roles...' })

        await reassignContactRoles(rolesForSelectedContactForReassignment)
      }
      if (targetsContactsWithRoleForRemoving.length) {
        await handleRelinkingRemovingRoles({
          targetsContactsWithRoleForRemoving,
        })
      }
    } catch (error) {
      Logger.error(ContactRelinkingError.ReassignRoles, error)
      NotificationController.showError({ message: ContactRelinkingError.ReassignRoles })
      setLoaderState({ isLoading: false, message: '' })
      return
    }

    try {
      if (flagsToReassign.filter((flag) => flag.contact).length > 0) {
        setLoaderState({ isLoading: true, message: 'Reassigning flags...' })
        await reassignFlags(flagsToReassign)
      }
    } catch (error) {
      Logger.error(ContactRelinkingError.ReassignFlags, error)
      NotificationController.showError({ message: ContactRelinkingError.ReassignFlags })
      setLoaderState({ isLoading: false, message: '' })
      return
    }

    try {
      const updatedHpRoles = relinkingContact.hpRoles.filter(
        (role) => !rolesForRemovingFromRelinkingContacts.includes(role)
      )

      await onRelink(updatedHpRoles)
    } catch (error) {
      Logger.error(ContactRelinkingError.RelinkContact, error)
      NotificationController.showError({ message: ContactRelinkingError.RelinkContact })
    } finally {
      setLoaderState({ isLoading: false, message: '' })
    }
  }, [
    flagsToReassign,
    onRelink,
    reassignFlags,
    relinkingContact,
    rolesForRemovingFromRelinkingContacts,
    rolesToReassign,
    targetsContactsWithRoleForRemoving,
    uniqueRolesToReassign,
  ])

  return { relinkContact, loaderState }
}
